import { useState, useEffect } from 'react';
import { FaVolumeUp, FaSpinner, FaImage } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male', kannada: 'ಪುಲ್ಲಿಂಗ' },
  { value: 'Female', label: 'Female', kannada: 'ಸ್ತ್ರೀಲಿಂಗ' },
  { value: 'Neuter', label: 'Neuter', kannada: 'ನಪುಂಸಕಲಿಂಗ' }
];

const GendersActivity = ({ activityId, initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState({
    words: [
      {
        word: '',
        englishWord: '',
        genderEnglish: '',
        genderKannada: '',
        languageAudio: null,
        englishAudio: null,
        image: null
      }
    ]
  });

  const [uploadingFiles, setUploadingFiles] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [generatingImages, setGeneratingImages] = useState(false);

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        if (parsed && Array.isArray(parsed.words)) {
          setContent(parsed);
        }
      } catch {
        // keep default
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  const buildStoragePath = ({ wordIndex, fileName }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;

    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'genders',
      fileName,
      itemId: `word${wordIndex + 1}`
    });
  };

  const playAudio = async (audioPath) => {
    if (!audioPath) return;

    try {
      const storage = getStorage();
      const audioRef = ref(storage, audioPath);
      const url = await getDownloadURL(audioRef);
      const audio = new Audio(url);
      audio.play().catch(() => {});
    } catch (error) {
      alert(`Failed to play audio. Error: ${error.message}`);
    }
  };

  const generateAllAudio = async () => {
    if (!content.words || content.words.length === 0) {
      alert('No words available to generate audio');
      return;
    }

    setGeneratingAudio(true);

    try {
      const storage = getStorage();

      const languageTexts = content.words.map(w => w.word).filter(Boolean);
      const englishTexts = content.words.map(w => w.englishWord).filter(Boolean);

      if (languageTexts.length === 0 && englishTexts.length === 0) {
        alert('No words found to generate audio. Please add words first.');
        setGeneratingAudio(false);
        return;
      }

      let audioGenerated = false;
      const updatedWords = [...(content.words || [])];

      // Generate audio for all language words
      if (languageTexts.length > 0) {
        const languageRequests = languageTexts.map(text => ({
          text,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE
        }));

        const languageAudioUrls = await generateBatchSpeechAudio(languageRequests, db, storage);

        let languageIndex = 0;
        for (let i = 0; i < updatedWords.length; i += 1) {
          if (updatedWords[i].word && updatedWords[i].word.trim()) {
            if (languageIndex < languageAudioUrls.length) {
              const audioPath = languageAudioUrls[languageIndex];
              if (audioPath && !audioPath.startsWith('[TTS Error:')) {
                updatedWords[i].languageAudio = audioPath;
                audioGenerated = true;
              }
            }
            languageIndex += 1;
          }
        }
      }

      // Generate audio for all English words
      if (englishTexts.length > 0) {
        const englishRequests = englishTexts.map(text => ({
          text,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE
        }));

        const englishAudioUrls = await generateBatchSpeechAudio(englishRequests, db, storage);

        let englishIndex = 0;
        for (let i = 0; i < updatedWords.length; i += 1) {
          if (updatedWords[i].englishWord && updatedWords[i].englishWord.trim()) {
            if (englishIndex < englishAudioUrls.length) {
              const audioPath = englishAudioUrls[englishIndex];
              if (audioPath && !audioPath.startsWith('[TTS Error:')) {
                updatedWords[i].englishAudio = audioPath;
                audioGenerated = true;
              }
            }
            englishIndex += 1;
          }
        }
      }

      setContent(prev => ({ ...prev, words: updatedWords }));

      if (audioGenerated) {
        alert('Audio generated successfully for all words!');
      } else {
        alert('Failed to generate audio. Please check the words and try again.');
      }
    } catch (error) {
      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  const generateMissingImages = async () => {
    if (!content.words || content.words.length === 0) {
      alert('No words available to generate images');
      return;
    }

    const hasMissingImages = content.words.some(w => !w.image);
    if (!hasMissingImages) {
      alert('All words already have images');
      return;
    }

    setGeneratingImages(true);

    try {
      const functions = getFunctions();
      const generateImagesFn = httpsCallable(functions, 'generateWordMeaningImages');
      
      await generateImagesFn({
        activityId,
        baseContent: JSON.stringify(content),
        chapterId,
        language: (selectedChapter?.subject || 'language').toLowerCase(),
        grade,
        activityType: 'GENDERS',
      });

      alert('Image generation started. Please refresh the page in a few moments to see the images.');
    } catch (error) {
      console.error('Failed to generate images:', error);
      alert('Failed to generate images: ' + error.message);
    } finally {
      setGeneratingImages(false);
    }
  };

  const updateWord = (index, field, value) => {
    const updated = [...(content.words || [])];
    updated[index] = { ...updated[index], [field]: value };
    setContent(prev => ({ ...prev, words: updated }));
  };

  const handleGenderChange = (index, genderValue) => {
    const option = GENDER_OPTIONS.find(g => g.value === genderValue);
    updateWord(index, 'genderEnglish', genderValue);
    updateWord(index, 'genderKannada', option ? option.kannada : '');
  };

  const addWord = () => {
    setContent(prev => ({
      ...prev,
      words: [
        ...(prev.words || []),
        { word: '', englishWord: '', genderEnglish: '', genderKannada: '', image: null }
      ]
    }));
  };

  const removeWord = async (index) => {
    const words = content.words || [];
    if (words.length <= 1) return;

    const word = words[index];
    const storage = getStorage();

    if (word.image) {
      try {
        const imgRef = ref(storage, word.image);
        await deleteObject(imgRef);
      } catch {
        // ignore delete errors
      }
    }

    const updated = words.filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, words: updated }));
  };

  const handleFileUpload = async (index, file) => {
    if (!file || !chapterId || !grade) return;

    const uploadKey = `${index}-image`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const words = content.words || [];
      const existingPath = words[index]?.image;

      if (existingPath) {
        try {
          const existingRef = ref(storage, existingPath);
          await deleteObject(existingRef);
        } catch {
          // ignore delete
        }
      }

      const storagePath = buildStoragePath({ wordIndex: index, fileName: file.name });
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);

      const updated = [...words];
      updated[index] = { ...updated[index], image: storagePath };
      setContent(prev => ({ ...prev, words: updated }));
    } catch {
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const previewImage = async (path) => {
    if (!path) return;
    try {
      const storage = getStorage();
      const imgRef = ref(storage, path);
      const url = await getDownloadURL(imgRef);
      window.open(url, '_blank');
    } catch (error) {
      alert(`Failed to preview image. Error: ${error.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Genders</h3>
            <p className="text-sm text-gray-600 mt-1">Add words with their grammatical gender, audio and an optional image.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-sm text-sm disabled:bg-purple-300 disabled:cursor-not-allowed"
              onClick={generateMissingImages}
              disabled={generatingImages || !(content.words && content.words.length)}
            >
              {generatingImages ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating Images...
                </>
              ) : (
                <>
                  <FaImage className="w-4 h-4 mr-2" />
                  Generate Missing Images
                </>
              )}
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm text-sm disabled:bg-emerald-300 disabled:cursor-not-allowed"
              onClick={generateAllAudio}
              disabled={generatingAudio || !(content.words && content.words.length)}
            >
              {generatingAudio ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FaVolumeUp className="w-4 h-4 mr-2" />
                  Generate Audio
                </>
              )}
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm text-sm"
              onClick={addWord}
            >
              + Add Word
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {(content.words || []).map((row, idx) => (
            <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {idx + 1}
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Word *"
                    value={row.word || ''}
                    onChange={e => updateWord(idx, 'word', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="English word"
                    value={row.englishWord || ''}
                    onChange={e => updateWord(idx, 'englishWord', e.target.value)}
                  />
                  <select
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    defaultValue={row.genderEnglish || ''}
                    onChange={e => handleGenderChange(idx, e.target.value)}
                  >
                    <option value="">Select Gender</option>
                    {GENDER_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-sm"
                    placeholder="Language gender"
                    value={row.genderKannada || ''}
                    readOnly
                  />
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id={`genders-img-${idx}`}
                    onChange={e => handleFileUpload(idx, e.target.files[0])}
                    disabled={uploadingFiles[`${idx}-image`]}
                  />
                  <label
                    htmlFor={`genders-img-${idx}`}
                    className="cursor-pointer px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs hover:bg-purple-100 transition-colors"
                  >
                    🖼️ Image
                  </label>
                  {uploadingFiles[`${idx}-image`] && (
                    <span className="text-purple-600 text-[10px]">Uploading...</span>
                  )}
                  {row.image && !uploadingFiles[`${idx}-image`] && (
                    <>
                      <button
                        type="button"
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        onClick={() => previewImage(row.image)}
                        title="Preview image"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        onClick={() => updateWord(idx, 'image', null)}
                        title="Remove image"
                      >
                        ✕
                      </button>
                    </>
                  )}
                  {row.languageAudio && (
                    <button
                      type="button"
                      className="p-1 text-green-600 hover:bg-green-100 rounded"
                      onClick={() => playAudio(row.languageAudio)}
                      title="Play language audio"
                    >
                      <FaVolumeUp className="w-3 h-3" />
                    </button>
                  )}
                  {row.englishAudio && (
                    <button
                      type="button"
                      className="p-1 text-green-600 hover:bg-green-100 rounded"
                      onClick={() => playAudio(row.englishAudio)}
                      title="Play English audio"
                    >
                      <FaVolumeUp className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {(content.words || []).length > 1 && (
                  <button
                    type="button"
                    className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                    onClick={() => removeWord(idx)}
                    title="Remove word"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const validateGendersContent = (content) => {
  const errors = [];

  if (!content.words || content.words.length === 0) {
    errors.push('At least one word is required.');
  }

  return errors;
};

export default GendersActivity;
